import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/use-toast";
import { getRoleString } from "@/lib/utils";
import { Loader2, UserPlus, X } from "lucide-react";
import { useState } from "react";
import {
  PendingInvite,
  Role,
} from "../../gen/rbt/cloud/v1alpha1/organization/organization_pb";
import {
  InvalidInputError,
  InvalidOperationError,
  useOrganization,
} from "../../gen/rbt/cloud/v1alpha1/organization/organization_rbt_react";

// Invitations expire after 7 days (must match backend).
// 7 days was chosen because this matches GitHub and NPM's invite expiration
// periods. If users ask for it, we could make this configurable in the future.
const INVITE_EXPIRATION_DAYS = 7;

function formatDate(timestamp: { seconds?: bigint; nanos?: number }): string {
  if (!timestamp.seconds) return "Unknown";
  const date = new Date(Number(timestamp.seconds) * 1000);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function getExpirationStatus(timestamp: { seconds?: bigint; nanos?: number }): {
  expired: boolean;
  text: string;
} {
  if (!timestamp.seconds) return { expired: false, text: "Unknown" };

  const invitedAt = new Date(Number(timestamp.seconds) * 1000);
  const expiresAt = new Date(
    invitedAt.getTime() + INVITE_EXPIRATION_DAYS * 24 * 60 * 60 * 1000
  );
  const now = new Date();

  if (now > expiresAt) {
    return { expired: true, text: "Expired" };
  }

  const daysRemaining = Math.ceil(
    (expiresAt.getTime() - now.getTime()) / (24 * 60 * 60 * 1000)
  );

  if (daysRemaining === 1) {
    return { expired: false, text: "Expires in 1 day" };
  }
  return { expired: false, text: `Expires in ${daysRemaining} days` };
}

interface InviteUserSectionProps {
  organizationId: string;
  currentUserId: string;
}

export function InviteUserSection({
  organizationId,
  currentUserId,
}: InviteUserSectionProps) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>(Role.EDITOR);
  const [isInviting, setIsInviting] = useState(false);
  const [cancellingEmail, setCancellingEmail] = useState<string | null>(null);
  const { toast } = useToast();

  const { useGetMembers, useGetPendingInvites, inviteMember, cancelInvite } =
    useOrganization({
      id: organizationId,
    });
  const { response: membersResponse } = useGetMembers({}, { suspense: true });
  const { response: pendingInvitesResponse } = useGetPendingInvites(
    {},
    { suspense: true }
  );

  if (!membersResponse || !pendingInvitesResponse) {
    return null;
  }

  const members = membersResponse.members;
  const pendingInvites = pendingInvitesResponse.pendingInvites;

  // Check if the current user is an owner.
  const currentUserRole = members[currentUserId]?.role;
  const isOwner = currentUserRole === Role.OWNER;

  // Only owners can invite users.
  if (!isOwner) {
    return null;
  }

  const handleInvite = async () => {
    if (!email.trim()) {
      toast({
        title: "Error",
        description: "Please enter an email address.",
        variant: "destructive",
      });
      return;
    }

    // Basic email validation.
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      toast({
        title: "Error",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }

    setIsInviting(true);
    try {
      const { aborted } = await inviteMember({
        email: email.trim().toLowerCase(),
        role: role,
      });

      if (aborted) {
        let errorMessage = "Failed to send invitation.";
        if (
          aborted.error instanceof InvalidInputError ||
          aborted.error instanceof InvalidOperationError
        ) {
          errorMessage = aborted.error.reason || errorMessage;
        }
        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Invitation Sent",
        description: `An invitation has been sent to ${email}.`,
      });
      setEmail("");
      setRole(Role.EDITOR);
    } catch (error) {
      console.error("Error sending invitation:", error);
      toast({
        title: "Error",
        description: "Failed to send invitation.",
        variant: "destructive",
      });
    } finally {
      setIsInviting(false);
    }
  };

  const handleCancelInvitation = async (invitedEmail: string) => {
    setCancellingEmail(invitedEmail);
    try {
      const { aborted } = await cancelInvite({
        email: invitedEmail,
      });

      if (aborted) {
        let errorMessage = "Failed to cancel invitation.";
        if (aborted.error instanceof InvalidOperationError) {
          errorMessage = aborted.error.reason || errorMessage;
        }
        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Invitation Cancelled",
        description: `Invitation to ${invitedEmail} has been cancelled.`,
      });
    } catch (error) {
      console.error("Error cancelling invitation:", error);
      toast({
        title: "Error",
        description: "Failed to cancel invitation.",
        variant: "destructive",
      });
    } finally {
      setCancellingEmail(null);
    }
  };

  // Convert the map to an array of entries for rendering.
  const pendingInviteEntries: [string, PendingInvite][] =
    Object.entries(pendingInvites);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Invite Users</CardTitle>
        <CardDescription>
          Invite new users to join your organization.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="user@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !isInviting) {
                    handleInvite();
                  }
                }}
                disabled={isInviting}
              />
            </div>
            <div>
              <Label htmlFor="role">Role</Label>
              <Select
                value={role.toString()}
                onValueChange={(value) => setRole(parseInt(value) as Role)}
                disabled={isInviting}
              >
                <SelectTrigger id="role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={Role.OWNER.toString()}>Owner</SelectItem>
                  <SelectItem value={Role.EDITOR.toString()}>Editor</SelectItem>
                  <SelectItem value={Role.VIEWER.toString()}>Viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Button
              onClick={handleInvite}
              disabled={isInviting || !email.trim()}
            >
              {isInviting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <UserPlus className="h-4 w-4 mr-2" />
              )}
              {isInviting ? "Sending..." : "Send Invitation"}
            </Button>
          </div>

          {pendingInviteEntries.length > 0 && (
            <>
              <div className="pt-4">
                <h4 className="text-sm font-semibold mb-2">
                  Pending Invitations
                </h4>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Invited</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingInviteEntries.map(([inviteEmail, invite]) => {
                    const expiration = invite.invitedAt
                      ? getExpirationStatus(invite.invitedAt)
                      : { expired: false, text: "Unknown" };
                    return (
                      <TableRow key={inviteEmail}>
                        <TableCell className="font-medium">
                          {inviteEmail}
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="capitalize">
                            {getRoleString(invite.role)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {invite.invitedAt
                            ? formatDate(invite.invitedAt)
                            : "Unknown"}
                        </TableCell>
                        <TableCell
                          className={
                            expiration.expired
                              ? "text-destructive"
                              : "text-muted-foreground"
                          }
                        >
                          {expiration.text}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCancelInvitation(inviteEmail)}
                            disabled={cancellingEmail === inviteEmail}
                            className="bg-background text-destructive hover:text-destructive hover:bg-background"
                          >
                            {cancellingEmail === inviteEmail ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <X className="h-4 w-4 mr-2" />
                            )}
                            Cancel
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
